import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  type ISignUpResult,
} from 'amazon-cognito-identity-js';

export type Role = 'admin' | 'agent';

export interface AuthUser {
  userId: string;
  email: string;
  role: Role;
  /** From the `given_name` Cognito attribute. May be empty if the admin
   *  didn't supply one at create time. */
  firstName?: string;
  /** From the `family_name` Cognito attribute. */
  lastName?: string;
}

export interface SignInSuccess {
  kind: 'success';
  user: AuthUser;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface NewPasswordRequired {
  kind: 'newPasswordRequired';
  cognitoUser: CognitoUser;
  userAttributes: Record<string, string>;
}

export type SignInResult = SignInSuccess | NewPasswordRequired;

let pool: CognitoUserPool | undefined;

function userPool(): CognitoUserPool {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    });
  }
  return pool;
}

function sessionToSuccess(user: CognitoUser, session: CognitoUserSession): SignInSuccess {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload() as Record<string, unknown>;
  const groups = payload['cognito:groups'];
  const role: Role = Array.isArray(groups) && groups.includes('Administrators') ? 'admin' : 'agent';
  const firstName = typeof payload.given_name === 'string' ? payload.given_name : undefined;
  const lastName = typeof payload.family_name === 'string' ? payload.family_name : undefined;
  return {
    kind: 'success',
    user: {
      userId: String(payload.sub ?? ''),
      email: String(payload.email ?? user.getUsername()),
      role,
      firstName,
      lastName,
    },
    idToken: idToken.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    expiresAt: idToken.getExpiration() * 1000,
  };
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool() });
  const auth = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(auth, {
      onSuccess: session => resolve(sessionToSuccess(cognitoUser, session)),
      onFailure: err => reject(err),
      newPasswordRequired: (userAttributes: Record<string, string>) => {
        // Cognito returns immutable attrs we must not echo back
        delete userAttributes.email_verified;
        delete userAttributes.email;
        resolve({ kind: 'newPasswordRequired', cognitoUser, userAttributes });
      },
    });
  });
}

export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>,
): Promise<SignInSuccess> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: session => resolve(sessionToSuccess(cognitoUser, session)),
      onFailure: err => reject(err),
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool() });
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
    });
  });
}

export function confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool() });
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
    });
  });
}

export function signOut(): void {
  const current = userPool().getCurrentUser();
  current?.signOut();
}

export function restoreSession(): Promise<SignInSuccess | undefined> {
  return new Promise(resolve => {
    const current = userPool().getCurrentUser();
    if (!current) return resolve(undefined);
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(undefined);
      resolve(sessionToSuccess(current, session));
    });
  });
}

// Type re-export so consumers don't import directly from amazon-cognito-identity-js.
export type { CognitoUser, ISignUpResult };
